/**
 * ChipFilterGroup - Toggle chip buttons for filters
 *
 * Features:
 * - Chips displayed in a flex-wrap layout
 * - Click to toggle selection
 * - Visual feedback for selected state
 * - Works with any label/value option array
 * - Supports both multi-select and single-select modes
 */

import { cn } from '@/lib/utils';

interface FilterOption {
  label: string;
  value: string;
}

interface ChipFilterGroupProps {
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  className?: string;
  /**
   * When true, only one chip can be selected at a time.
   * Clicking a selected chip deselects it, clicking another replaces the selection.
   * Default: false (multi-select mode)
   */
  singleSelect?: boolean;
}

export const ChipFilterGroup = ({
  options,
  selectedValues,
  onChange,
  className,
  singleSelect = false,
}: ChipFilterGroupProps) => {
  const handleChipClick = (value: string) => {
    if (singleSelect) {
      // Single-select: toggle off if already selected, otherwise select only this one
      const newValues = selectedValues.includes(value) ? [] : [value];
      onChange(newValues);
    } else {
      // Multi-select: toggle the value in the array
      const newValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      onChange(newValues);
    }
  };

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {options.map((option) => {
        const isSelected = selectedValues.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => handleChipClick(option.value)}
            className={cn(
              'px-2.5 py-1 text-xs font-medium rounded-full border transition-colors',
              isSelected
                ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 border-zinc-900 dark:border-zinc-100'
                : 'bg-white dark:bg-zinc-900 text-zinc-600 dark:text-zinc-400 border-zinc-950/10 dark:border-white/10 hover:bg-zinc-50 dark:hover:bg-zinc-800',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
